import { useState, useEffect, useCallback, useRef } from 'react'
import styled, { keyframes } from 'styled-components'
import { supabase } from '../supabase'
import colors from '../utils/style/colors'

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(100%); }
  to   { opacity: 1; transform: translateY(0); }
`
const popIn = keyframes`
  0%   { opacity: 0; transform: scale(0.85) translateY(20px); }
  70%  { transform: scale(1.03) translateY(0); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
`
const fadeOverlay = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`
const starPop = keyframes`
  0%   { transform: scale(1); }
  40%  { transform: scale(1.4) rotate(-8deg); }
  70%  { transform: scale(0.9) rotate(4deg); }
  100% { transform: scale(1) rotate(0); }
`
const successBounce = keyframes`
  0%   { transform: scale(0); opacity: 0; }
  60%  { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`


const Overlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(14, 26, 10, 0.45);
  z-index: 9998;
  display: flex; align-items: flex-end; justify-content: center;
  animation: ${fadeOverlay} 0.3s ease;
  backdrop-filter: blur(2px);
  @media (min-width: 600px) { align-items: center; }
`
const Card = styled.div`
  background: #fff;
  border-radius: 24px 24px 0 0;
  width: 100%; max-width: 440px;
  box-shadow: 0 -12px 60px rgba(0,0,0,0.2);
  animation: ${slideUp} 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
  font-family: ${colors.font};
  position: relative; overflow: hidden;
  @media (min-width: 600px) {
    border-radius: 24px;
    box-shadow: 0 32px 80px rgba(0,0,0,0.22);
    animation: ${popIn} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
`
const CardHeader = styled.div`
  background: linear-gradient(135deg, ${colors.vertOlive} 0%, #2a4520 100%);
  padding: 24px 28px 20px;
  position: relative; overflow: hidden;
  &::before {
    content: ''; position: absolute; top: -40px; right: -40px;
    width: 160px; height: 160px;
    background: radial-gradient(circle, rgba(200,168,75,0.2) 0%, transparent 70%);
  }
`
const CloseBtn = styled.button`
  position: absolute; top: 14px; right: 16px;
  background: rgba(255,255,255,0.15); border: none; cursor: pointer;
  width: 28px; height: 28px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.85rem; color: rgba(255,255,255,0.8);
  transition: background 0.2s; z-index: 2;
  &:hover { background: rgba(255,255,255,0.28); color: #fff; }
`
const HeaderEmoji = styled.div`font-size: 2rem; margin-bottom: 6px;`
const HeaderTitle = styled.h3`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.2rem; color: #fff; margin: 0 0 4px; position: relative; z-index: 1;
`
const HeaderSub = styled.p`
  font-size: 0.78rem; color: rgba(255,255,255,0.62); margin: 0; position: relative; z-index: 1;
`
const CardBody = styled.div`padding: 22px 28px 28px;`
const MobileTag = styled.div`
  display: inline-flex; align-items: center; gap: 5px;
  background: ${colors.beige}; border-radius: 20px;
  padding: 4px 10px; margin-bottom: 14px;
  font-size: 0.75rem; font-weight: 600; color: ${colors.texteLeger};
`
const Stars = styled.div`
  display: flex; justify-content: center; gap: 6px; margin-bottom: 6px;
`
const Star = styled.button`
  background: none; border: none; cursor: pointer;
  font-size: 2.2rem; line-height: 1; padding: 4px;
  transition: filter 0.15s;
  filter: ${p => p.$active ? 'none' : 'grayscale(1) opacity(0.25)'};
  animation: ${p => p.$justClicked ? starPop : 'none'} 0.4s ease;
  &:hover { filter: none; }
  &:active { transform: scale(0.88); }
`
const RatingLabel = styled.div`
  text-align: center; height: 20px; margin-bottom: 14px;
  font-size: 0.86rem; font-weight: 700; color: ${colors.vertOlive};
  transition: all 0.2s;
`
const Textarea = styled.textarea`
  width: 100%; box-sizing: border-box;
  border: 1.5px solid ${colors.beigeFonce};
  border-radius: 12px; padding: 10px 14px;
  font-family: ${colors.font}; font-size: 0.88rem; color: ${colors.texte};
  resize: none; outline: none; min-height: 70px;
  transition: border-color 0.2s, box-shadow 0.2s;
  background: ${colors.blanc};
  &:focus { border-color: ${colors.vertOlive}; box-shadow: 0 0 0 3px rgba(58,90,46,0.08); }
  &::placeholder { color: ${colors.texteLeger}; }
`
const SubmitBtn = styled.button`
  width: 100%; padding: 14px;
  background: ${p => p.disabled ? colors.beigeFonce : `linear-gradient(135deg, ${colors.vertOlive}, #2a4520)`};
  color: ${p => p.disabled ? colors.texteLeger : '#fff'};
  border: none; border-radius: 12px;
  font-family: ${colors.font}; font-size: 0.9rem; font-weight: 700;
  cursor: ${p => p.disabled ? 'default' : 'pointer'};
  margin-top: 14px; transition: all 0.2s;
  position: relative; overflow: hidden;
  ${p => !p.disabled && `
    &:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(58,90,46,0.35); }
    &:active { transform: translateY(0); }
  `}
`
const SuccessWrap = styled.div`padding: 40px 28px 36px; text-align: center;`
const SuccessEmoji = styled.div`
  font-size: 3rem; margin-bottom: 12px;
  animation: ${successBounce} 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
`
const SuccessTitle = styled.h3`
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 1.3rem; color: ${colors.texte}; margin: 0 0 6px;
`
const SuccessSub = styled.p`font-size: 0.85rem; color: ${colors.texteLeger}; margin: 0;`

const STORAGE_KEY = 'feedback_last_shown'
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000
const TIMER_MS = 2.5 * 60 * 1000
const LABELS = ['😞 Décevant', '😕 Passable', '😐 Correct', '😊 Bien', '🤩 Excellent !']
const isMobile = () => window.innerWidth < 600 || 'ontouchstart' in window

export default function FeedbackPopup() {
  const [visible, setVisible] = useState(false)
  const [rating, setRating] = useState(0)
  const [hover, setHover] = useState(0)
  const [clickedStar, setClickedStar] = useState(0)
  const [comment, setComment] = useState('')
  const [sent, setSent] = useState(false)
  const [sending, setSending] = useState(false)
  const [triggered, setTriggered] = useState(false)
  const [mobile] = useState(isMobile)
  const lastScrollY = useRef(0)
  const scrollVelocity = useRef([])

  const shouldShow = useCallback(() => {
    const path = window.location.pathname
    if (path.startsWith('/admin') || path.startsWith('/suivi-devis')) return false
    const last = localStorage.getItem(STORAGE_KEY)
    if (last && Date.now() - parseInt(last) < ONE_WEEK_MS) return false
    return true
  }, [])

  const show = useCallback(() => {
    if (triggered) return
    if (!shouldShow()) return
    setTriggered(true)
    setVisible(true)
  }, [triggered, shouldShow])

  useEffect(() => {
    const timer = setTimeout(show, TIMER_MS)

    // Desktop : souris vers le haut
    const handleMouseLeave = (e) => { if (!isMobile() && e.clientY <= 10) show() }
    document.addEventListener('mouseleave', handleMouseLeave)

    // Mobile : scroll rapide vers le haut
    const handleScroll = () => {
      if (!isMobile()) return
      const currentY = window.scrollY
      const delta = lastScrollY.current - currentY
      lastScrollY.current = currentY
      scrollVelocity.current.push(delta)
      if (scrollVelocity.current.length > 5) scrollVelocity.current.shift()
      const avg = scrollVelocity.current.reduce((a, b) => a + b, 0) / scrollVelocity.current.length
      if (avg > 40 && currentY < 300) show()
    }
    window.addEventListener('scroll', handleScroll, { passive: true })

    // Mobile : onglet en arrière-plan
    const handleVisibility = () => { if (document.hidden && isMobile()) show() }
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('mouseleave', handleMouseLeave)
      window.removeEventListener('scroll', handleScroll)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [show])

  const close = () => {
    setVisible(false)
    localStorage.setItem(STORAGE_KEY, Date.now().toString())
  }

  const handleStar = (n) => {
    setRating(n)
    setClickedStar(n)
    setTimeout(() => setClickedStar(0), 400)
  }

  const submit = async () => {
    if (!rating) return
    setSending(true)
    await supabase.from('site_feedback').insert([{
      rating,
      comment: comment.trim() || null,
      page: window.location.pathname,
      is_mobile: isMobile(),
    }])
    setSending(false)
    setSent(true)
    localStorage.setItem(STORAGE_KEY, Date.now().toString())
    setTimeout(() => setVisible(false), 2800)
  }

  if (!visible) return null

  return (
    <Overlay onClick={e => e.target === e.currentTarget && close()}>
      <Card>
        {sent ? (
          <SuccessWrap>
            <SuccessEmoji>🙏</SuccessEmoji>
            <SuccessTitle>Merci pour votre retour !</SuccessTitle>
            <SuccessSub>Votre avis nous aide à améliorer le site.</SuccessSub>
          </SuccessWrap>
        ) : (
          <>
            <CardHeader>
              <CloseBtn onClick={close}>✕</CloseBtn>
              <HeaderEmoji>{mobile ? '📱' : '💬'}</HeaderEmoji>
              <HeaderTitle>
                {mobile ? 'Navigation mobile ?' : "Comment s'est passée votre visite ?"}
              </HeaderTitle>
              <HeaderSub>
                {mobile
                  ? "Quelques secondes pour nous aider à améliorer l'expérience mobile"
                  : '30 secondes pour nous aider à améliorer le site'}
              </HeaderSub>
            </CardHeader>
            <CardBody>
              {mobile && <MobileTag>📱 Vous naviguez sur mobile</MobileTag>}
              <Stars>
                {[1,2,3,4,5].map(n => (
                  <Star
                    key={n}
                    $active={n <= (hover || rating)}
                    $justClicked={clickedStar === n}
                    onClick={() => handleStar(n)}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                  >⭐</Star>
                ))}
              </Stars>
              <RatingLabel>{hover || rating ? LABELS[(hover || rating) - 1] : ''}</RatingLabel>
              <Textarea
                placeholder={mobile
                  ? 'Un commentaire sur la navigation mobile ? (optionnel)'
                  : 'Un commentaire ? (optionnel)'}
                value={comment}
                onChange={e => setComment(e.target.value)}
                maxLength={500}
              />
              <SubmitBtn onClick={submit} disabled={!rating || sending}>
                {sending ? 'Envoi…' : rating ? `Envoyer — ${LABELS[rating-1]}` : 'Sélectionnez une note'}
              </SubmitBtn>
            </CardBody>
          </>
        )}
      </Card>
    </Overlay>
  )
}
